import React, { ReactElement, useState } from 'react';
import { Box, Grid } from '@mui/material';
import {
  ScheduledCorpusItem,
  ScheduledCorpusItemsResult,
  ActivitySource,
  useRescheduleScheduledCorpusItemMutation,
} from '../../../api/generatedTypes';
import {
  ScheduleDayFilterOptions,
  ScheduleDayHeading,
  ScheduledItemCardWrapper,
} from '../';
import { getDisplayTopic } from '../../helpers/topics';
import { useNotifications, useRunMutation } from '../../../_shared/hooks';

interface ScheduleDayDataProps {
  /**
   * The GUID of the current surface, e.g. "NEW_TAB_EN_US"
   */
  currentScheduledSurfaceGuid: string;

  /**
   * Data for a given date.
   */
  data: ScheduledCorpusItemsResult;

  /**
   * A function that triggers a new API call to refetch the data for a given
   * query. Needed on the Schedule page to refresh data after every action.
   */
  refetch?: VoidFunction;

  /**
   * Callback to set the current item (to work on, e.g. edit or reschedule)
   * on the Schedule Page.
   */
  setCurrentItem: React.Dispatch<
    React.SetStateAction<Omit<ScheduledCorpusItem, '__typename'> | undefined>
  >;

  /**
   * A toggle function for the "Edit this item" modal.
   */
  toggleEditModal: VoidFunction;

  /**
   * A toggle function for the "Remove this item" modal.
   */
  toggleRemoveModal: VoidFunction;

  /**
   * A toggle function for the "Schedule this item" modal.
   */
  toggleScheduleItemModal: VoidFunction;

  /**
   * A toggle function for the "Reject and unschedule this item" modal.
   */
  toggleRejectAndUnscheduleModal: VoidFunction;
}

/**
 * Show all scheduled entries for a given date.
 *
 * @param props
 * @constructor
 */
export const ScheduleDayData: React.FC<ScheduleDayDataProps> = (
  props,
): ReactElement => {
  const {
    currentScheduledSurfaceGuid,
    data,
    refetch,
    setCurrentItem,
    toggleEditModal,
    toggleRemoveModal,
    toggleScheduleItemModal,
    toggleRejectAndUnscheduleModal,
  } = props;

  // Set up the toast message hook
  const { showNotification } = useNotifications();

  // Get a helper function that will execute each mutation, show standard notifications
  // and execute any additional actions in a callback
  const { runMutation } = useRunMutation();

  // Prepare the "reschedule scheduled curated corpus item" mutation
  const [rescheduleItem] = useRescheduleScheduledCorpusItemMutation();

  /**
   * Pocket Hits items need to be returned in a pre-defined order. When the graph
   * is queried, scheduled items are returned sorted by the `updatedAt` value
   * in ascending order (most recently updated goes last).
   *
   * To enable the curators to rearrange stories in a certain order for a Pocket Hits
   * email, a "Move to bottom" button has been added that updates the scheduled entry
   * to bump up the timestamp recorded in the `updatedAt` field.
   * @param item
   */
  const moveItemToBottom = (item: ScheduledCorpusItem): void => {
    // Run the "reschedule" mutation with the same variables
    // it already has. All we want from it is to update `updatedAt` timestamp.
    const variables = {
      externalId: item.externalId,
      scheduledDate: item.scheduledDate,
      source: item.source,
    };

    // Run the mutation
    runMutation(
      rescheduleItem,
      { variables },
      `Success! Item moved to the bottom of the list.`,
      undefined,
      undefined,
      refetch,
    );
  };

  const initialFiltersState: ScheduleDayFilterOptions = {
    topics: 'All',
    publishers: 'All',
    types: 'All',
  };
  const [filters, setFilters] =
    useState<ScheduleDayFilterOptions>(initialFiltersState);

  return (
    <>
      <Box mt={9} mb={3}>
        <ScheduleDayHeading
          data={data}
          setFilters={setFilters}
          key={`{data.scheduledDate}-day-heading`}
        />
      </Box>

      <Grid
        container
        alignItems="stretch"
        spacing={3}
        justifyContent="flex-start"
        key={data.scheduledDate}
      >
        <Grid item xs={12}>
          <Grid container spacing={2}>
            {data.items.map((item: ScheduledCorpusItem) => {
              if (
                (filters.topics === 'All' &&
                  filters.publishers === 'All' &&
                  filters.types === 'All') ||
                filters.topics === getDisplayTopic(item.approvedItem.topic) ||
                filters.publishers === item.approvedItem.publisher ||
                (filters.types === 'Ml' &&
                  item.source === ActivitySource.Ml &&
                  !item.approvedItem.isSyndicated) ||
                (filters.types == 'ML-Syndicated' &&
                  item.source === ActivitySource.Ml &&
                  item.approvedItem.isSyndicated) ||
                (filters.types === 'Collections' &&
                  item.approvedItem.isCollection) ||
                (filters.types === 'Syndicated' &&
                  item.approvedItem.isSyndicated) ||
                (filters.types === 'Time-sensitive' &&
                  item.approvedItem.isTimeSensitive)
              ) {
                return (
                  <ScheduledItemCardWrapper
                    key={item.externalId}
                    item={item}
                    onEdit={() => {
                      setCurrentItem(item);
                      toggleEditModal();
                    }}
                    onUnschedule={() => {
                      setCurrentItem(item);
                      toggleRemoveModal();
                    }}
                    onMoveToBottom={() => {
                      moveItemToBottom(item);
                    }}
                    onReschedule={() => {
                      setCurrentItem(item);
                      toggleScheduleItemModal();
                    }}
                    onReject={() => {
                      setCurrentItem(item);

                      // If this item is also scheduled elsewhere, show an error.
                      if (
                        item.approvedItem.scheduledSurfaceHistory.length > 1
                      ) {
                        showNotification(
                          'Cannot reject and unschedule this item - multiple scheduled entries exist.',
                          'error',
                        );
                      }
                      // Otherwise, proceed with rejecting and unscheduling this item
                      else {
                        toggleRejectAndUnscheduleModal();
                      }
                    }}
                    currentScheduledDate={data.scheduledDate}
                    scheduledSurfaceGuid={currentScheduledSurfaceGuid}
                  />
                );
              }
            })}
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};
